import { useEffect } from 'react';
import { CSSObject } from '@emotion/react';
import { ButtonSection as ButtonSectionType } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import ContentEditable from 'react-contenteditable';
import { i18next } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useFormField } from '@frontend/design-system';
import { useComposerSection } from '../../content-composer/hooks/use-composer-section';
import { SectionBase } from '../../content-composer/section-base/section-base';
import type { ComposerSection, Corners } from '../../content-composer/types';
import { getCornersStyle } from '../utils/get-corners-style';
import { getFrameStyle } from '../utils/get-frame-style';
import { ButtonOutput } from './button-output';
import { ButtonSettings } from './button-settings';
import { ButtonTool } from './button-tool';
import { ButtonToolbar } from './button-toolbar';
import { ButtonValidator } from './button-validator';

export const ButtonSection: ComposerSection<ButtonSectionType> = ({ buttonColor, corners, frame, link, textColor }) => {
  const { setProps } = useComposerSection<ButtonSectionType>();

  const fieldProps = useFormField({
    type: 'text',
    value: link?.text,
  });

  useEffect(() => {
    if (fieldProps.value === link?.text) return;
    const newLink = {
      ...(link ? { ...link, text: fieldProps.value } : { linkType: '', text: fieldProps.value, url: '' }),
    };
    setProps((props) => (props.link = newLink));
  }, [fieldProps.value, link, setProps]);

  return (
    <SectionBase css={getFrameStyle(frame)}>
      <ContentEditable
        id={fieldProps.id}
        html={fieldProps.value}
        onChange={fieldProps.onChange}
        css={[
          getCornersStyle<CSSObject>(corners as Corners | undefined),
          {
            alignItems: 'center',
            background: buttonColor,
            color: textColor,
            display: 'flex',
            fontWeight: theme.font.weight.bold,
            height: theme.spacing(5),
            justifyContent: 'center',
            outline: 'none',
            padding: theme.spacing(1, 2),
            '&:hover, &:focus': {
              boxShadow: theme.shadows.floating,
            },
          },
        ]}
      />
      <ButtonToolbar link={link} />
    </SectionBase>
  );
};

ButtonSection.craft = {
  props: {
    buttonColor: theme.colors.primary50.toUpperCase(),
    corners: 'pill',
    frame: {
      alignment: 'center',
      backgroundColor: theme.colors.neutral5.toUpperCase(),
      paddingBottom: theme.spacing(3),
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(3),
    },
    link: {
      linkType: 'custom',
      text: i18next.t('Click to Edit Button', { ns: 'content-composer' }),
      url: '',
    },
    sectionType: 'ButtonSection',
    textColor: theme.colors.white.toUpperCase(),
  },
  related: {
    output: ButtonOutput,
    settings: ButtonSettings,
    tool: ButtonTool,
    // validator: ButtonValidator, TODO: Change back to this! Adjust types so it works like this.
    validator: {
      errors: ButtonValidator.errors,
      validate: ({ context, props, reportError }) =>
        ButtonValidator.validate({
          context,
          errors: ButtonValidator.errors,
          props,
          reportError,
        }),
    },
  },
};
